import type { LabRoutingModel, LabRoutingState } from '.';
import { computed, effect, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import type { FirmRef } from 'src/admin/firms/FirmOverview/source';
import { firmDisplayName } from 'src/admin/firms/FirmOverview/standing';
import type { LabRoutingSource } from 'src/admin/LAB/Routing/source';
import { DisposableBoxMap } from 'src/admin/lib/DisposableBoxMap';
import { observeOutcome } from 'src/admin/lib/queryOutcome';
import type { Outcome } from 'src/admin/lib/queryOutcome';
import { toFirmRef, toProviderLite, toRouteLite } from 'src/admin/lib/sourceRecords';
import { restricted } from 'src/admin/lib/visible';
import type { Visible } from 'src/admin/lib/visible';
import { AdminViewerScope } from 'src/admin/models/AdminViewerScope/AdminViewerScope';
import { inject, injectable } from 'src/features/ioc';
import { getFirmQuery, getFirmsQuery } from 'src/queries/firms';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import { getAllRoutesQuery } from 'src/queries/labRoutes';
import type { ReactBindings } from 'src/types/bindings';

const LOADING: LabRoutingState = { status: 'loading' };

/** One `GET /firms/:id`, for naming a firm that owns a row but is not among the viewer's own firms. */
class FirmLookup implements Disposable {
    readonly #stack = new DisposableStack();
    readonly outcome: ReadonlySignal<Outcome<FirmWithParentsAndChildren>>;

    constructor(firmId: string, queryObserverModelFactory: ReactBindings['QueryObserverModelFactory']) {
        this.outcome = observeOutcome(
            this.#stack,
            queryObserverModelFactory,
            computed(() => getFirmQuery(firmId)),
        );
    }

    [Symbol.dispose](): void {
        this.#stack.dispose();
    }
}

@injectable()
export class LabRoutingModelImpl implements LabRoutingModel {
    readonly #stack = new DisposableStack();
    readonly #viewer: AdminViewerScope;
    /** One `GET /firms/:id` per owner the viewer's firms do not name. */
    readonly #lookups: DisposableBoxMap<FirmLookup>;

    readonly state: ReadonlySignal<LabRoutingState>;
    readonly canElevate: ReadonlySignal<boolean>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('ActiveUserModel') activeUserModel: ReactBindings['ActiveUserModel'],
    ) {
        this.#lookups = this.#stack.use(new DisposableBoxMap<FirmLookup>());
        this.#viewer = this.#stack.use(new AdminViewerScope({ queryObserverModelFactory, activeUserModel }));
        this.canElevate = this.#viewer.canElevate;
        const isSuperuser = this.#viewer.isSuperuser;

        // `GET /v1/routes` and `GET /v1/providers` with no firm filter span every firm the viewer can read.
        const routes = observeOutcome(
            this.#stack,
            queryObserverModelFactory,
            computed(() => getAllRoutesQuery()),
        );
        const providers = observeOutcome(
            this.#stack,
            queryObserverModelFactory,
            computed(() => getAllProvidersQuery()),
        );
        // Superusers administer every firm, and `GET /firms` names them all in one request.
        const allFirms = observeOutcome(
            this.#stack,
            queryObserverModelFactory,
            computed(() => ({ ...getFirmsQuery(), enabled: isSuperuser.value })),
        );

        // Names known without asking: the viewer's own firms and the refs they expose, plus every firm for superusers.
        const knownNames = computed(() => {
            const names = new Map<string, string>();
            const add = (ref: FirmRef) => {
                if (!names.has(ref.id)) names.set(ref.id, firmDisplayName(ref));
            };
            const standing = this.#viewer.standing.value;
            if (standing.status === 'ok') {
                for (const firm of standing.data.memberFirms) {
                    add(firm);
                    [...(firm.parents ?? []), ...(firm.children ?? [])].forEach(add);
                }
            }
            const everyFirm = allFirms.value;
            if (isSuperuser.value && everyFirm.status === 'ok') {
                for (const firm of everyFirm.data) add(firm);
            }
            return names;
        });

        // Owners of visible rows whose names are not known get one `GET /firms/:id` each, once the lists are in.
        const unknownOwnerIds = computed(() => {
            // Wait for the viewer's firms (and every firm, for superusers) so only truly unknown owners are asked about.
            if (this.#viewer.standing.value.status !== 'ok') return new Set<string>();
            if (isSuperuser.value && allFirms.value.status === 'pending') return new Set<string>();
            const routeList = routes.value;
            const providerList = providers.value;
            if (routeList.status !== 'ok' || providerList.status !== 'ok') return new Set<string>();
            const owners = new Set([
                ...routeList.data.map((route) => route.firmId),
                ...providerList.data.map((provider) => provider.firmId),
            ]);
            const known = knownNames.value;
            return new Set([...owners].filter((id) => !known.has(id)));
        });

        this.#stack.defer(
            effect(() => {
                const wanted = unknownOwnerIds.value;
                untracked(() => this.#lookups.reconcile(wanted, (id) => new FirmLookup(id, queryObserverModelFactory)));
            }),
        );

        this.state = computed<LabRoutingState>(() => {
            const viewer = this.#viewer.standing.value;
            if (viewer.status === 'failed') return { status: 'error', error: viewer.error };
            if (viewer.status !== 'ok') return LOADING;
            const standing = viewer.data;

            const everyFirm = allFirms.value;
            if (isSuperuser.value) {
                if (everyFirm.status === 'failed') return { status: 'error', error: everyFirm.error };
                if (everyFirm.status === 'pending') return LOADING;
            }
            const routeList = routes.value;
            const providerList = providers.value;
            if (routeList.status === 'failed') return { status: 'error', error: routeList.error };
            if (providerList.status === 'failed') return { status: 'error', error: providerList.error };
            if (routeList.status !== 'ok' || providerList.status !== 'ok') return LOADING;

            const lookups = this.#lookups.current.value;
            const lookupNames = new Map<string, Visible<string>>();
            for (const id of unknownOwnerIds.value) {
                // A name still in flight, or one broken lookup, never holds the page; the owner is unnamed for now.
                const outcome = lookups.get(id)?.outcome.value ?? { status: 'pending' as const };
                lookupNames.set(
                    id,
                    outcome.status === 'ok'
                        ? firmDisplayName(outcome.data)
                        : restricted(
                              outcome.status === 'pending'
                                  ? 'loading'
                                  : outcome.status === 'failed'
                                    ? 'unavailable'
                                    : 'other-firm',
                          ),
                );
            }

            const names = knownNames.value;
            const firmNames: Record<string, Visible<string>> = {
                ...Object.fromEntries(names),
                ...Object.fromEntries(lookupNames),
            };
            const knownFirmIds = new Set(Object.keys(firmNames));

            const scopeFirms: FirmRef[] =
                isSuperuser.value && everyFirm.status === 'ok'
                    ? everyFirm.data.map(toFirmRef)
                    : standing.ownedFirms().map(toFirmRef);

            const source: LabRoutingSource = {
                viewer: standing.context,
                scopeFirms,
                routes: routeList.data.map((route) => toRouteLite(route, knownFirmIds)),
                providers: providerList.data.map((provider) => toProviderLite(provider, knownFirmIds)),
                firmNames,
            };
            return { status: 'ready', source };
        });
    }

    [Symbol.dispose](): void {
        this.#stack.dispose();
    }
}
